import React, { useMemo } from 'react';
import { layoutResponse, splitRecommendation } from './layoutResponse';
import { Headline, Caveat, SignalStrip, Section, Sections, Recommendations, Sources } from './primitives';

/**
 * ResponseBody — renders the parsed blocks of one assistant turn in the AI
 * Response Pattern order: headline · caveats · signals · lead · sections ·
 * actions. The parser (AIChat.renderMarkdown) owns markdown → blocks; this
 * owns blocks → anatomy. Unstructured replies (no sections, no signals, no
 * data view) fall through to plain prose so a greeting never gets a 20px
 * headline.
 *
 * @param {Array}    blocks     typed blocks from renderMarkdown
 * @param {boolean}  streaming  answer is still arriving
 * @param {Function} inline     inline-markdown renderer (bold/links) for list bodies
 * @param {boolean}  openSections render sections expanded (design prop sectionsOpenByDefault)
 */
export default function ResponseBody({ blocks, streaming = false, inline = (t) => t, openSections = false }) {
  const layout = useMemo(() => layoutResponse(blocks), [blocks]);
  const { headline, caveats, signals, lead, sections, actions, structured } = layout;

  const renderBlock = (b, i) => {
    if (!b) return null;
    if (b.kind === 'list') {
      const Tag = b.ordered ? 'ol' : 'ul';
      return (
        <Tag key={`list-${i}`} className={`rp-list${b.ordered ? ' is-ordered' : ''}`}>
          {b.items.map((it, j) => <li key={j}>{it.el}</li>)}
        </Tag>
      );
    }
    if (b.kind === 'caveat') return <Caveat key={`cav-${i}`} tone={b.tone}>{b.el}</Caveat>;
    if (b.kind === 'signals') return <SignalStrip key={`sig-${i}`} signals={b.signals} />;
    if (b.kind === 'heading') return b.el;
    return b.el ? <React.Fragment key={`b-${i}`}>{b.el}</React.Fragment> : null;
  };

  if (!structured) {
    // Plain prose: same markup as before the pattern existed.
    return <>{blocks.map(renderBlock)}</>;
  }

  const renderSection = (sec, i) => {
    const key = `sec-${i}-${sec.label}`;
    let body;
    if (sec.isRecommend) {
      const listIdx = sec.blocks.findIndex(b => b.kind === 'list');
      const list = sec.blocks[listIdx];
      const before = sec.blocks.slice(0, listIdx);
      const after = sec.blocks.slice(listIdx + 1);
      const items = list.items.map(it => {
        const { title, body: bodyText } = splitRecommendation(it.text);
        return { title: inline(title), body: bodyText ? inline(bodyText) : null };
      });
      body = (
        <>
          {before.length > 0 && <div style={{ padding: '14px 16px 0' }}>{before.map(renderBlock)}</div>}
          <Recommendations items={items} />
          {after.length > 0 && <div style={{ padding: '0 16px 14px' }}>{after.map(renderBlock)}</div>}
        </>
      );
      return (
        <Section key={key} id={key} label={sec.label} count={sec.count} countTone={sec.countTone}
          preview={sec.preview} hint={sec.hint} defaultOpen={openSections} flush>
          {body}
        </Section>
      );
    }
    if (sec.isSources) {
      const lines = [];
      for (const b of sec.blocks) {
        if (b.kind === 'p' || b.kind === 'caveat') lines.push(b.el);
        else if (b.kind === 'list') b.items.forEach(it => lines.push(it.el));
        else lines.push(renderBlock(b, lines.length));
      }
      return (
        <Section key={key} id={key} label={sec.label} preview={sec.preview} defaultOpen={openSections} bodyClassName="is-sources">
          <Sources lines={lines} />
        </Section>
      );
    }
    return (
      <Section key={key} id={key} label={sec.label} count={sec.count} countTone={sec.countTone}
        preview={sec.preview} hint={sec.hint} defaultOpen={openSections}>
        {sec.blocks.map(renderBlock)}
      </Section>
    );
  };

  return (
    <div className={`rp${streaming ? ' is-streaming' : ''}`}>
      {headline && <Headline>{headline.inline || headline.el}</Headline>}
      {caveats.map((c, i) => <Caveat key={`caveat-${i}`} tone={c.tone}>{c.el}</Caveat>)}
      {signals && <SignalStrip signals={signals} />}
      {lead.length > 0 && <div className="rp-lead">{lead.map(renderBlock)}</div>}
      {sections.length > 0 && <Sections>{sections.map(renderSection)}</Sections>}
      {actions.length > 0 && <div className="rp-actions">{actions.map((a, i) => <React.Fragment key={`act-${i}`}>{a.el}</React.Fragment>)}</div>}
    </div>
  );
}
